// Typed wrappers around every /api/v1/assessment/* endpoint the
// risk-assessment feature consumes.
//
// We intentionally re-use the project-wide ``apiClient`` (see
// src/api/client.ts) instead of constructing a fresh axios instance:
//   * The shared client already attaches the WP-issued JWT on every
//     request, refuses to transmit credentials over plain HTTP in
//     production, and centralises 401 handling (clears the token and
//     bounces to the WP login bridge once).
//   * Spinning up a second axios reading ``import.meta.env.VITE_API_URL``
//     directly would either skip those guarantees or duplicate them —
//     both bad.
//
// This module is the SINGLE point of contact between the feature UI and
// the backend; nothing under ``components/`` should import from
// ``apiClient`` or ``axios`` directly.

import { AxiosError } from 'axios';

import { apiClient } from '../../../api/client';

// ---------------------------------------------------------------------------
// Wire types
//
// Every shape below mirrors the corresponding Pydantic schema in
// ``backend/app/schemas/schema_assessment.py`` / the response built by
// ``backend/app/api/api_risk_assessment.py``. Keep the two in sync.
// ---------------------------------------------------------------------------

/** A single RTQ question — alphabetic A-E options keyed to a label. */
export interface RtqQuestion {
    id: number;
    question: string;
    answers: Record<string, string>; // { A: "...", B: "...", ... }
}

/** RTQ payload shape returned by GET /assessment/questionnaires. */
export interface RtqQuestionnaire {
    scoring: {
        scale: Record<string, number>;
        risk_profiles: { range: string; profile: string }[];
    };
    questions: RtqQuestion[];
}

/** Behavioral / FC question — numeric 1..N scale with per-option labels. */
export interface ScaleQuestion {
    text: string;
    category: string;
    scale: Record<string, string>; // { "1": "...", "2": "...", ... }
}

/** Style question — bare 1..5 likert (agree/disagree), no per-option labels. */
export interface StyleQuestion {
    id: string;
    text: string;
    weight: number;
    direction: 'active' | 'passive';
}

export interface QuestionnairesResponse {
    rtq: RtqQuestionnaire;
    behavioral: Record<string, ScaleQuestion>; // keyed by Q1..Q20
    fc: Record<string, ScaleQuestion>;         // keyed by FC1..FC5
    style: StyleQuestion[];
}

/** Verbatim answers payload — the four keys the backend expects. */
export interface RawAnswers {
    rtq_answers: Record<string, string>;       // { "1": "C", ... }
    behavioral_answers: Record<string, number>; // { "Q1": 3, ... }
    fc_answers: Record<string, number>;         // { "FC1": 3, ... }
    style_answers: Record<string, number>;      // { "sq1": 3, ... }
}

export interface RiskScores {
    capacity: number;
    behavioral: number;
    tolerance: number;
    total: number;
}

export interface InsightCategory {
    /** New name (data-driven plan); kept alongside ``average``. */
    score: number;
    /** Legacy name preserved for back-compat; same value as ``score``. */
    average: number;
    /** "low" | "moderate" | "high" — drives the colour coding. */
    level: string;
    /** Human-readable copy from insights.json. */
    description: string;
    /** Legacy alias of ``description`` (same string). */
    insight: string;
    /** Question IDs that contributed to this category — used for drill-down. */
    question_ids: string[];
}

export interface CalculateProfileResponse {
    user: string;
    risk_scores: RiskScores;
    risk_profile: string;
    portfolio: {
        allocation: Record<string, number>;
        persona: Record<string, unknown>;
        metrics: Record<string, unknown>;
    };
    persona: Record<string, unknown>;
    style_score: number;
    insights: {
        emotional_psychological: InsightCategory;
        behavioral: InsightCategory;
    };
}

/**
 * Body for POST /assessment/save-results. ``raw_answers`` is optional
 * on the wire so callers that auto-saved via /save-progress can omit
 * it and let the backend keep the existing draft on file.
 */
export interface SaveResultsBody {
    risk_scores: RiskScores;
    risk_profile: string;
    portfolio: CalculateProfileResponse['portfolio'];
    persona: CalculateProfileResponse['persona'];
    raw_answers?: RawAnswers;
    /**
     * Forwarded verbatim from the /calculate-full-profile response so
     * the persisted row carries everything the results dashboard needs
     * to re-render (Style-score pill + behavioral radar) on a returning
     * user's "View Latest Results" visit. Made required (not optional)
     * in the TS contract so we catch any caller drift at compile time;
     * the backend tolerates omission for legacy callers but the SPA
     * must always include both.
     */
    style_score: number;
    insights: CalculateProfileResponse['insights'];
}

/**
 * Persisted ``assessment_result`` row returned by GET /assessment/results.
 * Every column except ``user_id`` is nullable on the backend (a row may
 * exist with only ``raw_answers`` populated when the user has just
 * auto-saved a partial draft) so every field here is optional.
 */
export interface PersistedAssessmentResult {
    risk_scores: RiskScores | null;
    risk_profile: string | null;
    portfolio: CalculateProfileResponse['portfolio'] | null;
    persona: CalculateProfileResponse['persona'] | null;
    raw_answers: RawAnswers | null;
    /**
     * Mirror the live profile response so the dashboard hydrates the
     * Style pill and radar chart directly from the persisted row. NULL
     * on rows that pre-date the 0005_style_insights backend migration --
     * the SPA falls back to empty defaults + a "retake to refresh" hint
     * in that case.
     */
    style_score: number | null;
    insights: CalculateProfileResponse['insights'] | null;
    updated_at?: string;
}

// ---------------------------------------------------------------------------
// Cold-start tolerance
//
// The backend may run on a host whose free / low tier spins the
// container down after a period of inactivity (this was Render's
// ``starter`` plan in the original deployment and is similar on most
// PaaS providers). The FIRST request after a spin-down hits the edge
// proxy while the container is still booting and is answered with a
// bare ``502 Bad Gateway`` that does NOT carry the
// ``Access-Control-Allow-Origin`` header — because the backend's
// CORSMiddleware never runs. From the browser's point of view this
// looks identical to a real CORS misconfiguration ("No
// 'Access-Control-Allow-Origin' header is present on the requested
// resource"), but the actual cause is the cold start: a manual retry
// a few seconds later succeeds with full CORS headers in place.
//
// We harden the two GETs that fire on mount (`/questionnaires` and
// `/results`, see RiskAssessmentFlow + RiskLandingPage) so a cold start
// no longer surfaces as "Could not load the questionnaire". The other
// endpoints (calculate / save / report) are user-initiated AFTER the
// boot path has already warmed the container, so they don't need this
// treatment.
const COLD_START_TIMEOUT_MS = 60_000; // covers a typical container warm-up
const COLD_START_RETRY_DELAY_MS = 2_000;

function isLikelyColdStart(err: unknown): boolean {
    // Axios reports a CORS-blocked / network-level failure as an
    // AxiosError with NO ``response`` (the browser drops the body
    // before axios sees it). Timeouts surface the same way with
    // ``code === 'ECONNABORTED'``. Both shapes mean "no usable HTTP
    // response was received" — exactly the cold-start signature.
    if (!(err instanceof AxiosError)) return false;
    return !err.response;
}

async function withColdStartRetry<T>(fn: () => Promise<T>): Promise<T> {
    try {
        return await fn();
    } catch (err) {
        if (!isLikelyColdStart(err)) throw err;
        // eslint-disable-next-line no-console
        console.warn(
            '[riskApi] boot call failed without a response — likely a ' +
                'backend cold start. Retrying once after a short delay.',
        );
        await new Promise((resolve) =>
            setTimeout(resolve, COLD_START_RETRY_DELAY_MS),
        );
        return fn();
    }
}

// ---------------------------------------------------------------------------
// Endpoint wrappers
// ---------------------------------------------------------------------------

export const riskApi = {
    /** GET /assessment/questionnaires — payload for the four forms. */
    async getQuestionnaires(): Promise<QuestionnairesResponse> {
        const { data } = await withColdStartRetry(() =>
            apiClient.get<QuestionnairesResponse>('/assessment/questionnaires', {
                timeout: COLD_START_TIMEOUT_MS,
            }),
        );
        return data;
    },

    /**
     * POST /assessment/save-progress — auto-save a partial draft.
     *
     * Sends every sub-dict the user has touched so far; the backend
     * persists ONLY the ``raw_answers`` column and never overwrites
     * the user's previously-completed scoring columns.
     *
     * Note: the backend schema (``AssessmentProgress``) accepts the
     * four answer dicts as TOP-LEVEL keys, NOT wrapped in a
     * ``raw_answers`` envelope -- so we forward the object verbatim.
     */
    async saveProgress(rawAnswers: Partial<RawAnswers>): Promise<void> {
        await apiClient.post('/assessment/save-progress', rawAnswers);
    },

    /**
     * POST /assessment/calculate-full-profile — score the completed
     * questionnaire. The backend rejects partial submissions with a
     * 422 whose ``data.missing_ids`` field lists the unanswered
     * question IDs in canonical order; callers should surface this
     * to the user instead of treating it as a generic failure.
     */
    async calculateProfile(payload: RawAnswers): Promise<CalculateProfileResponse> {
        const { data } = await apiClient.post<CalculateProfileResponse>(
            '/assessment/calculate-full-profile',
            payload,
        );
        return data;
    },

    /** POST /assessment/save-results — persist the completed profile. */
    async saveFullResults(body: SaveResultsBody): Promise<void> {
        await apiClient.post('/assessment/save-results', body);
    },

    /**
     * GET /assessment/results — read the user's persisted row. Returns
     * ``null`` when the user has no saved row at all (the API answers
     * with 404, which we translate so callers can branch on a regular
     * value instead of catching).
     */
    async getLatestResults(): Promise<PersistedAssessmentResult | null> {
        try {
            const { data } = await withColdStartRetry(() =>
                apiClient.get<PersistedAssessmentResult>('/assessment/results', {
                    timeout: COLD_START_TIMEOUT_MS,
                }),
            );
            return data;
        } catch (err) {
            if (err instanceof AxiosError && err.response?.status === 404) {
                return null;
            }
            throw err;
        }
    },

    /** DELETE /assessment/reset — clear the persisted row. */
    async reset(): Promise<void> {
        await apiClient.delete('/assessment/reset');
    },

    /**
     * POST /assessment/report/download — stream the branded PDF report
     * for a completed assessment. Returns a ``Blob`` so the caller can
     * trigger a browser download via an object URL without ever
     * writing the bytes to disk on the client.
     *
     * The endpoint runs the same scoring pipeline as
     * /calculate-full-profile, so it requires the full 43-answer
     * payload; partial drafts are rejected with a 422.
     */
    async downloadReport(payload: RawAnswers): Promise<Blob> {
        const response = await apiClient.post(
            '/assessment/report/download',
            payload,
            { responseType: 'blob' },
        );
        return response.data as Blob;
    },
};

/**
 * Narrow helper that pulls the ``missing_ids`` array out of a 422
 * response from /calculate-full-profile. Used by the wizard so it
 * can scroll the user back to the first unanswered question instead
 * of just showing a generic error toast.
 */
export function extractMissingIds(err: unknown): string[] | null {
    if (err instanceof AxiosError && err.response?.status === 422) {
        const data = err.response.data as
            | { data?: { missing_ids?: string[] } }
            | undefined;
        return data?.data?.missing_ids ?? null;
    }
    return null;
}
